import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import {TABLE_DEFAULT_COLUMN_WIDTH} from 'notebook/tables/constants/TableLayoutConstants';
import APIUtil from 'util/APIUtil';

const FREEZE_MERGE_MESSAGES: Record<'merge' | 'freeze' | 'unfreeze', string> = {
  merge: 'Unmerge cells that span the freeze boundary first.',
  freeze: 'Cannot freeze through a merged cell. Unmerge first.',
  unfreeze: 'Cannot unfreeze through a merged cell. Unmerge first.',
};

/** Warns that `action` was refused because a merged cell spans the freeze boundary. */
export function showFreezeMergeConflictToast(action: 'merge' | 'freeze' | 'unfreeze'): void {
  ToastActions.pushToast({message: FREEZE_MERGE_MESSAGES[action], type: ToastType.WARNING});
}

/** Returns true if any merge cell spans across the boundary after column `j`. */
export function hasMergeCrossingColumn(cells: Array<Array<Cell>>, j: number): boolean {
  for (const row of cells) {
    for (let col = 0; col <= j; col++) {
      const span = row[col]?.mergeColSpan;
      if (span != null && span > 1 && col + span - 1 > j) {
        return true;
      }
    }
  }
  return false;
}

/** Returns true if a column selection [minJ, maxJ] straddles the freeze boundary. */
export function selectionStraddlesFreezeBoundary(
  minJ: number,
  maxJ: number,
  freezeBoundary: number
): boolean {
  return freezeBoundary > 0 && minJ < freezeBoundary && maxJ >= freezeBoundary;
}

export interface ReorderFreezeParams {
  selectionStart: number;
  selectionEnd: number;
  insertIndex: number;
  siBoundary: number;
  effectiveFrozen: number;
  /**
   * Must be `TableUtil.getUserFreezeBoundary`, not `getFreezeBoundary`: an adapter-supplied
   * `defaultAffixedColumnCount` affixes columns positionally and needs no adjustment, so passing it
   * here would turn a reorder into an explicit `frozenColumnCount` that overrides the adapter.
   */
  absoluteFreezeBoundary: number;
}

/**
 * Computes the new frozenColumnCount after columns are reordered via drag.
 * Returns null if no change is needed.
 */
export function computeFrozenCountAfterReorder(params: ReorderFreezeParams): number | null {
  const {selectionStart, selectionEnd, insertIndex, siBoundary, effectiveFrozen, absoluteFreezeBoundary} =
    params;
  if (absoluteFreezeBoundary <= 0) {
    return null;
  }

  const numMoved = selectionEnd - selectionStart + 1;
  // Overlap of [selectionStart, selectionEnd] with the frozen zone [siBoundary, absoluteFreezeBoundary - 1]
  const frozenInSelection = Math.max(
    0,
    Math.min(selectionEnd, absoluteFreezeBoundary - 1) - Math.max(selectionStart, siBoundary) + 1
  );
  const unfrozenInSelection = numMoved - frozenInSelection;

  let newFrozenCount = effectiveFrozen;
  if (insertIndex >= absoluteFreezeBoundary && frozenInSelection > 0) {
    newFrozenCount -= frozenInSelection;
  } else if (insertIndex < absoluteFreezeBoundary && unfrozenInSelection > 0) {
    newFrozenCount += unfrozenInSelection;
  }

  if (newFrozenCount !== effectiveFrozen) {
    return Math.max(newFrozenCount, 0);
  }
  return null;
}

/** Whether user-controlled freeze/unfreeze is supported for tables in this document type. */
export function supportsUserFreezeColumns(fileId: string | null | undefined): boolean {
  if (!fileId) {
    return false;
  }
  return (
    APIUtil.isBaseEntry(fileId) ||
    APIUtil.isEntryOrSubOrWorkflowTemplate(fileId) ||
    APIUtil.isProtocol(fileId) ||
    APIUtil.isBiologicalEntity(fileId)
  );
}

/**
 * Returns true if freezing through column `j` would leave insufficient room
 * to display at least the next unfrozen column.
 */
export function wouldFreezeExceedViewport(
  widths: Array<number | null>,
  siBoundary: number,
  j: number,
  containerWidth: number
): boolean {
  const frozenWidth = widths
    .slice(siBoundary, j + 1)
    .reduce<number>((a, b) => a + (b ?? TABLE_DEFAULT_COLUMN_WIDTH), 0);
  const nextColumnWidth = widths[j + 1] ?? TABLE_DEFAULT_COLUMN_WIDTH;
  return containerWidth - frozenWidth < nextColumnWidth;
}
